import type { IntroData } from '@wix/ambassador-funnel-intro-v1-intro-data/types'
import { useContent, useExternalTransition } from '@wix/funnel-builder-lizard'
import { forwardFunnelQueryParams, tryCatch } from '@wix/funnel-utils'
import { useEffect, useState } from 'react'

import { createAccountInvites } from '../api/AccountInvitesService/createAccountInvites'
import type { Invite } from '../api/AccountInvitesService/types'
import { saveAgencyName } from '../api/AccountService'
import { saveRecentIntroData } from '../api/FunnelIntroHydraService/saveRecentIntroData'
import { parsePageBiSlug, useFlowLogger } from '../bi/mfi'
import { resolveCategoryFromBusinessTerm } from '../category'
import { useStorageContext } from '../context/StorageContext/StorageContext'
import { flushPanorama } from '../monitoring/panorama'

type State = { creatingAccountInvites?: 'pending' | 'success' }

export const useFinishFlowV2 = (forcePlatform: string | undefined) => {
  const externalTransitionUrl = useExternalTransition()
  const [statuses, setStatuses] = useState<State>()
  const { biSlug } = useContent()
  const flowLogger = useFlowLogger()
  const { appDefIds, category, businessName, logo, accountInvites, isWixStudioAccount, agencyName, isDeveloper } =
    useStorageContext()

  useEffect(() => {
    if (!externalTransitionUrl) {
      return
    }

    const makeRequests = async (accountInvites?: Invite[], businessName?: string, agencyName?: string) => {
      if (accountInvites) {
        setStatuses(() => ({ creatingAccountInvites: 'pending' }))
        await tryCatch(() => createAccountInvites(accountInvites))
      }
      setStatuses({ creatingAccountInvites: 'success' })
      const introData: IntroData = {
        category: introDataCategory,
        selectedApps,
        businessName,
        logo,
        industryId,
        structureId,
        isDeveloper,
      }
      if (forcePlatform === 'harmony') {
        introData.isHarmony = true
      } else if (forcePlatform === 'wix') {
        introData.goals = [{ goal: 'wix' }]
      }
      await Promise.allSettled([saveRecentIntroData(introData), agencyName && saveAgencyName(agencyName)])
      flushPanorama()
      window.location.href = targetUrl
    }

    const targetUrl = forwardFunnelQueryParams(externalTransitionUrl)

    const [screenName, screenVersion] = parsePageBiSlug(biSlug)
    void flowLogger.finishFlow({ targetUrl, screenName, screenVersion })

    const businessTerm = category?.businessTerm
    const introDataCategory = resolveCategoryFromBusinessTerm(businessTerm, category?.businessSegmentId)
    const { industryId, structureId } = introDataCategory
    const selectedApps = appDefIds?.map(appDefId => ({ appDefId })) ?? []

    void makeRequests(accountInvites, businessName, agencyName)
  }, [
    isDeveloper,
    accountInvites,
    agencyName,
    appDefIds,
    biSlug,
    businessName,
    logo,
    category?.businessTerm,
    flowLogger,
    isWixStudioAccount,
    externalTransitionUrl,
  ])

  return statuses
}
